import { ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import { useStoreActions } from '~/store/hooks';
import { IGetClientRequestPayload } from '~/store/client/types';
import { toDay } from '~/utils/date/date';
import { extractErrorCode, extractErrorMessage } from '~/utils/error/error';
import { useApiMutation } from '~/hooks/api';
import { IMedicaidPlan } from '~/services/api/medicaidPlan/types';
import Button from '~/ui/components/common/Button';
import Modal from '~/ui/components/common/Modal';
import DatePicker from '~/ui/components/inputs/DatePicker';

import { EXPIRATION_DATE_ERROR_CODES } from '~/ui/pages/Clients/reusable/PersonalDetails/MedicaidPlan/constants';
import {
  archivePlanResolver,
  getEarliestExpirationDate,
  IArchivePlanFormValues,
  IArchivePlanValidationContext,
} from './validate';

import styles from './ArchiveMedicaidPlanModal.module.scss';

interface IArchiveMedicaidPlanModalProps {
  plan: IMedicaidPlan;
  requestInfo: IGetClientRequestPayload;
  onClose: () => void;
  onArchived: () => void;
}

const ArchiveMedicaidPlanModal = ({
  plan,
  requestInfo,
  onClose,
  onArchived,
}: IArchiveMedicaidPlanModalProps): ReactElement => {
  const { onArchiveMedicaidPlan } = useStoreActions(actions => actions.client);
  const showError = useStoreActions(actions => actions.snackbar.showError);

  const today = new Date();
  const earliest = getEarliestExpirationDate(plan.effectiveDate);

  const {
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<IArchivePlanFormValues, IArchivePlanValidationContext>({
    defaultValues: { expirationDate: '' },
    resolver: archivePlanResolver,
    context: { effectiveDate: plan.effectiveDate },
  });

  const { mutate: archivePlan } = useApiMutation(
    ({ expirationDate }: IArchivePlanFormValues) =>
      onArchiveMedicaidPlan({
        requestInfo,
        requestPayload: { expirationDate: toDay(expirationDate) ?? expirationDate },
      }),
    {
      notifyMessage: 'Medicaid Plan archived',
      onSuccess: () => onArchived(),
      throwOnError: false,
      onError: error => {
        if (EXPIRATION_DATE_ERROR_CODES.includes(extractErrorCode(error) ?? '')) {
          setError('expirationDate', { type: 'server', message: extractErrorMessage(error) });
        } else {
          showError(extractErrorMessage(error));
        }
      },
    },
  );

  return (
    <Modal open preventOutsideClick maxWidth={false} className={styles.paper} onClose={onClose}>
      <section className={styles.container}>
        <h2 className={styles.title}>Archive Medicaid Plan</h2>
        <form className={styles.form} onSubmit={handleSubmit(archivePlan)}>
          <DatePicker
            name="expirationDate"
            label="Expiration Date *"
            openTo="date"
            minDate={earliest?.toISOString()}
            maxDate={today.toISOString()}
            control={control}
            errors={errors}
          />
          <div className={styles.actions}>
            <Button type="button" color="primary" variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isSubmitting || !watch('expirationDate')}
              isLoading={isSubmitting}
            >
              Archive Plan
            </Button>
          </div>
        </form>
      </section>
    </Modal>
  );
};

export default ArchiveMedicaidPlanModal;
